import { relations, sql } from 'drizzle-orm'
import {
  decimal,
  index,
  integer,
  pgEnum,
  pgTable,
  text,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getNullableIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { callingCampaigns } from './callingCampaign'
import { customers } from './customer'
import { opportunities } from './opportunity'
import { users } from './user'

export const phoneCallDirectionEnum = pgEnum('phone_call_direction', [
  'inbound',
  'outbound',
])

export const phoneCallRoutingStageEnum = pgEnum('phone_call_routing_stage', [
  'ringing',
  'erp_answered',
  'mobile_forwarded',
  'mobile_answered',
  'voicemail',
  'abandoned',
  'completed',
])

export const phoneCallRecordingStatusEnum = pgEnum(
  'phone_call_recording_status',
  [
    'pending',
    'recording',
    'saved',
    'transcribing',
    'summarized',
    'skipped',
    'failed',
  ],
)

// Outbound call outcome. Populated by:
//   1. Deterministic hooks (booked_visit when a visit is created from the
//      call)
//   2. AI classifier (from transcript summary) at high confidence
//   3. Operator manual disposition (always wins if both exist)
// Drives lead-dedup logic in the calling queue.
//
// Note: `abandoned` here is distinct from phone_call_routing_stage.abandoned
// (which means the call itself rang out unanswered). The outcome value means
// "we've decided to stop contacting this lead in this campaign" — either
// because the no_answer attempt cap was reached, or because the operator
// explicitly gave up via the callback modal's "Lopeta yrittäminen" action.
// The accompanying callbackReason captures *why* (no_answer / no_slots /
// customer_requested_later).
export const phoneCallOutcomeEnum = pgEnum('phone_call_outcome', [
  'no_answer',
  'reached_not_interested',
  'reached_callback_later',
  'booked_visit',
  'do_not_call',
  'unclear',
  'abandoned',
])

// Who wrote the current `outcome` value. Preserves audit when AI
// auto-commits but operator later overrides. `auto` is a deterministic
// system inference (e.g. ring-no-answer hangup → outcome='no_answer'); used
// when neither AI transcript nor operator click is available.
export const phoneCallOutcomeSourceEnum = pgEnum('phone_call_outcome_source', [
  'ai',
  'operator',
  'auto',
])

// Orthogonal to `outcome`. Captures *why* a call was postponed or abandoned:
//   - outcome = 'reached_callback_later' → why the operator scheduled a retry
//   - outcome = 'abandoned'              → why the operator / cap gave up
// Stats can aggregate supply-side (no_slots_available) vs demand-side
// (no_answer / customer_requested_later) reasons across both outcomes.
// Required in the UI for callback modal; nullable in the DB for back-compat
// with rows written before the reason prompt existed.
export const phoneCallCallbackReasonEnum = pgEnum(
  'phone_call_callback_reason',
  ['no_answer', 'customer_requested_later', 'no_slots_available'],
)

export interface PhoneCallLeg {
  callControlId: string
  userId: number | null
  kind: 'pool' | 'mobile'
  status: 'ringing' | 'answered' | 'cancelled' | 'no_answer' | 'failed'
}

export type PhoneCallActionItem = string

export const phoneCalls = pgTable(
  'phone_calls',
  {
    ...getBaseFields(),
    telnyxCallControlId: text().notNull(),
    direction: phoneCallDirectionEnum().notNull(),
    fromNumber: text().notNull(),
    toNumber: text().notNull(),

    // External party on the call (caller for inbound, callee for outbound).
    // Null = unclaimed inbound from unknown number, or outbound to a number
    // that doesn't match any user.
    userId: getNullableIdReference(() => users.id),

    // Internal ERP user handling the call (pickup for inbound, dialer for
    // outbound). Null for inbound until someone picks up.
    answererUserId: getNullableIdReference(() => users.id),

    // Entity context. Set on outbound calls from an opportunity/customer
    // page, and on "Luo liidi tästä puhelusta" relink.
    opportunityId: getNullableIdReference(() => opportunities.id),
    customerId: getNullableIdReference(() => customers.id),

    // Set when the call was initiated from a calling campaign queue so we
    // can attribute outcomes (and booked visits) back to the campaign.
    callingCampaignId: getNullableIdReference(() => callingCampaigns.id),

    // Committed outcome. See phoneCallOutcomeEnum.
    outcome: phoneCallOutcomeEnum(),
    outcomeSource: phoneCallOutcomeSourceEnum(),
    // When outcome='reached_callback_later', this is when the lead should
    // resurface in the calling queue.
    callbackAt: getTimestampWithTimezone(),
    // When outcome='reached_callback_later', why the operator postponed.
    // Always nulled when outcome becomes anything else, so stats queries
    // can group by (outcome, callback_reason) safely.
    callbackReason: phoneCallCallbackReasonEnum(),
    // Freeform operator note captured at disposition time (not the AI
    // summary — that's aiSummary).
    operatorNotes: text(),

    // AI's independent classification. Kept alongside the committed outcome
    // so an operator override never wipes the original model reading.
    aiSuggestedOutcome: phoneCallOutcomeEnum(),
    aiOutcomeConfidence: decimal({ precision: 3, scale: 2 }),
    aiOutcomeReasoning: text(),
    aiSuggestedCallbackAt: getTimestampWithTimezone(),

    routingStage: phoneCallRoutingStageEnum().notNull().default('ringing'),

    startedAt: getTimestampWithTimezone().notNull().defaultNow(),
    answeredAt: getTimestampWithTimezone(),
    endedAt: getTimestampWithTimezone(),
    durationSeconds: integer(),

    recordingUrl: text(),
    recordingStatus: phoneCallRecordingStatusEnum(),

    transcript: text(),

    // Transient buffer for Telnyx real-time transcription chunks. Appended to
    // on each `call.transcription` event with `is_final=true`, then nulled on
    // `call.hangup` once the Gemini summary has been produced — we never
    // persist the verbatim transcript long-term.
    transcriptBuffer: text(),

    aiSummary: text(),
    aiActionItems: typedJsonb<PhoneCallActionItem[]>('ai_action_items'),

    // Set when an operator confirms an unanswered call has been followed up
    // (callback placed, message actioned). Distinct from routing stage because
    // the call itself is already over — this tracks the human follow-up.
    handledAt: getTimestampWithTimezone(),

    legs: typedJsonb<PhoneCallLeg[]>('legs')
      .notNull()
      .default(sql`'[]'::jsonb`),
  },
  (table) => [
    ...getBaseIndexes('phone_calls', table),
    uniqueIndex('phone_calls_telnyx_call_control_id_idx')
      .on(table.telnyxCallControlId)
      .where(sql`deleted_at IS NULL`),
    index('phone_calls_user_id_idx').on(table.userId),
    index('phone_calls_answerer_user_id_idx').on(table.answererUserId),
    index('phone_calls_opportunity_id_idx').on(table.opportunityId),
    index('phone_calls_customer_id_idx').on(table.customerId),
    index('phone_calls_calling_campaign_id_idx').on(table.callingCampaignId),
    index('phone_calls_callback_at_idx').on(table.callbackAt),
    index('phone_calls_started_at_idx').on(table.startedAt),
    index('phone_calls_routing_stage_idx').on(table.routingStage),
  ],
)

export const phoneCallsRelations = relations(phoneCalls, ({ one }) => ({
  user: one(users, {
    fields: [phoneCalls.userId],
    references: [users.id],
    relationName: 'phoneCallUser',
  }),
  answerer: one(users, {
    fields: [phoneCalls.answererUserId],
    references: [users.id],
    relationName: 'phoneCallAnswerer',
  }),
  opportunity: one(opportunities, {
    fields: [phoneCalls.opportunityId],
    references: [opportunities.id],
  }),
  customer: one(customers, {
    fields: [phoneCalls.customerId],
    references: [customers.id],
  }),
  callingCampaign: one(callingCampaigns, {
    fields: [phoneCalls.callingCampaignId],
    references: [callingCampaigns.id],
  }),
}))
